/**
 * JSON Schema for AI Service Chatbot Responses
 * 
 * This defines the expected structure for responses from the ai-service
 * to enable rich, structured rendering in the chatbot UI
 */

export const RESPONSE_TYPES = {
  TEXT: 'text',
  CART_SUMMARY: 'cart_summary',
  ORDER_LIST: 'order_list',
  ORDER_DETAILS: 'order_details',
  PRODUCT_LIST: 'product_list',
  PRODUCT_DETAILS: 'product_details',
  PRODUCT_SELECTION: 'product_selection',
  ADDRESS_LIST: 'address_list',
  ACCOUNT_DETAILS: 'account_details',
  RETURN_LIST: 'return_list',
  RETURN_DETAILS: 'return_details',
  ACTION_BUTTONS: 'action_buttons',
  ERROR: 'error'
};

/**
 * Base Response Schema
 * All responses should follow this structure
 */
export const BaseResponseSchema = {
  agentId: 'string', // Agent identifier (e.g., 'frontendAgent')
  sessionId: 'string', // Session identifier
  message: 'string', // Fallback text message
  type: 'string', // One of RESPONSE_TYPES
  data: 'object|null', // Structured data based on type
  timestamp: 'string' // ISO timestamp
};

/**
 * Cart Summary Response
 * Used when showing cart contents
 */
export const CartSummarySchema = {
  type: RESPONSE_TYPES.CART_SUMMARY,
  data: {
    totalItems: 'number',
    shops: [
      {
        shopId: 'string',
        shopName: 'string',
        shopLogo: 'string|null',
        items: [
          {
            productId: 'string',
            name: 'string',
            image: 'string|null',
            quantity: 'number',
            price: 'number',
            currency: 'string'
          }
        ],
        subtotal: 'number',
        currency: 'string'
      }
    ],
    grandTotal: 'number',
    currency: 'string'
  }
};

/**
 * Order List Response
 * Used when showing user's orders
 */
export const OrderListSchema = {
  type: RESPONSE_TYPES.ORDER_LIST,
  data: {
    orders: [
      {
        orderId: 'string',
        orderNumber: 'string',
        date: 'string', // ISO date
        status: 'string',
        statusColor: 'string', // hex color or tailwind class
        total: 'number',
        currency: 'string',
        itemCount: 'number',
        shopName: 'string',
        previewItems: [
          {
            name: 'string',
            image: 'string|null',
            quantity: 'number'
          }
        ]
      }
    ],
    pagination: {
      page: 'number',
      totalPages: 'number',
      totalItems: 'number'
    }
  }
};

/**
 * Order Details Response
 * Used when showing specific order details
 */
export const OrderDetailsSchema = {
  type: RESPONSE_TYPES.ORDER_DETAILS,
  data: {
    orderId: 'string',
    orderNumber: 'string',
    date: 'string',
    status: 'string',
    statusColor: 'string',
    customer: {
      name: 'string',
      email: 'string'
    },
    shipping: {
      address: 'string',
      method: 'string',
      trackingNumber: 'string|null'
    },
    items: [
      {
        productId: 'string',
        name: 'string',
        image: 'string|null',
        quantity: 'number',
        price: 'number',
        total: 'number'
      }
    ],
    summary: {
      subtotal: 'number',
      shipping: 'number',
      tax: 'number',
      total: 'number',
      currency: 'string'
    }
  }
};

/**
 * Product List Response
 * Used when showing product recommendations or search results
 */
export const ProductListSchema = {
  type: RESPONSE_TYPES.PRODUCT_LIST,
  data: {
    products: [
      {
        productId: 'string',
        name: 'string',
        description: 'string|null',
        image: 'string|null',
        price: 'number',
        originalPrice: 'number|null', // for discounts
        currency: 'string',
        availability: 'string', // 'in_stock', 'out_of_stock', 'limited'
        rating: 'number|null', // 0-5
        reviewCount: 'number|null',
        brand: 'string|null',
        category: 'string|null'
      }
    ],
    context: 'string|null' // Context message like "Based on your search for..."
  }
};

/**
 * Product Selection Response
 * Used when user needs to choose between product variants/options
 * Supports both simple variants and variant groups with multiple items
 */
export const ProductSelectionSchema = {
  type: RESPONSE_TYPES.PRODUCT_SELECTION,
  data: {
    message: 'string', // Context message like "Please select your preferred option"
    product: {
      productId: 'string',
      name: 'string',
      description: 'string|null',
      image: 'string|null',
      basePrice: 'number|null', // Base price if applicable
      currency: 'string'
    },
    // Support for both simple variants and variant groups
    variants: [
      {
        // Simple variant structure (backward compatible)
        variantId: 'string',
        name: 'string', // e.g., "Size: Large", "Color: Blue", "Material: Cotton"
        description: 'string|null',
        image: 'string|null',
        price: 'number',
        originalPrice: 'number|null', // for discounts
        currency: 'string',
        availability: 'string', // 'in_stock', 'out_of_stock', 'limited'
        attributes: 'object', // e.g., { size: 'L', color: 'blue', material: 'cotton' }
        isRecommended: 'boolean|null' // highlight recommended option
      }
    ],
    // New variant groups structure for hierarchical selection
    variantGroups: [
      {
        groupId: 'string',
        message: 'string', // Description of this variant group
        description: 'string|null', // Additional details
        items: [
          {
            itemId: 'string',
            name: 'string',
            description: 'string|null',
            image: 'string|null',
            price: 'number',
            originalPrice: 'number|null',
            currency: 'string',
            availability: 'string',
            attributes: 'object',
            isRecommended: 'boolean|null'
          }
        ]
      }
    ],
    selectionType: 'string', // 'single', 'multiple', 'quantity'
    maxSelections: 'number|null', // for multiple selection
    instructions: 'string|null' // Additional instructions for selection
  }
};

/**
 * Address List Response
 * Used when showing user's saved addresses
 */
export const AddressListSchema = {
  type: RESPONSE_TYPES.ADDRESS_LIST,
  data: {
    addresses: [
      {
        addressId: 'string',
        name: 'string',
        company: 'string|null',
        addressLine1: 'string',
        addressLine2: 'string|null',
        city: 'string',
        state: 'string',
        postalCode: 'string',
        country: 'string',
        countryCode: 'string', // ISO country code
        tags: 'string[]', // ['shipping', 'billing', 'default']
        isDefault: 'boolean'
      }
    ],
    message: 'string|null' // Context message like "Here are your saved addresses"
  }
};

/**
 * Account Details Response
 * Used when showing user's account information
 */
export const AccountDetailsSchema = {
  type: RESPONSE_TYPES.ACCOUNT_DETAILS,
  data: {
    personalInfo: {
      name: 'string',
      email: 'string',
      phone: 'string|null',
      company: 'string|null',
      customerNumber: 'string',
      businessModel: 'string', // 'B2B', 'B2C'
      preferredLanguage: 'string',
      preferredCurrency: 'string',
      preferredSite: 'string',
      lastLogin: 'string' // ISO date
    },
    addresses: [
      {
        addressId: 'string',
        name: 'string',
        addressLine1: 'string',
        city: 'string',
        state: 'string',
        postalCode: 'string',
        country: 'string',
        tags: 'string[]' // ['shipping', 'billing', 'default']
      }
    ],
    message: 'string|null' // Context message like "Here are the details about your account"
  }
};

/**
 * Return List Response
 * Used when showing user's return requests
 */
export const ReturnListSchema = {
  type: RESPONSE_TYPES.RETURN_LIST,
  data: {
    returns: [
      {
        id: 'string',
        approvalStatus: 'string', // 'PENDING', 'APPROVED', 'REJECTED', 'PROCESSED'
        received: 'boolean',
        orders: [
          {
            id: 'string',
            items: [
              {
                id: 'string',
                name: 'string',
                quantity: 'number',
                reason: {
                  code: 'string',
                  details: 'string'
                },
                unitPrice: {
                  value: 'number',
                  currency: 'string'
                },
                total: {
                  value: 'number',
                  currency: 'string'
                }
              }
            ]
          }
        ],
        reason: {
          code: 'string',
          details: 'string'
        },
        total: {
          value: 'number',
          currency: 'string'
        },
        expiryDate: 'string', // ISO date
        metadata: {
          createdAt: 'string', // ISO date
          modifiedAt: 'string', // ISO date
          version: 'number'
        }
      }
    ],
    message: 'string|null' // Context message like "Here are your return requests"
  }
};

/**
 * Return Details Response
 * Used when showing specific return request details
 */
export const ReturnDetailsSchema = {
  type: RESPONSE_TYPES.RETURN_DETAILS,
  data: {
    id: 'string',
    approvalStatus: 'string',
    received: 'boolean',
    orders: [
      {
        id: 'string',
        items: [
          {
            id: 'string',
            name: 'string',
            quantity: 'number',
            reason: {
              code: 'string',
              details: 'string'
            },
            unitPrice: {
              value: 'number',
              currency: 'string'
            },
            total: {
              value: 'number',
              currency: 'string'
            }
          }
        ]
      }
    ],
    reason: {
      code: 'string',
      details: 'string'
    },
    total: {
      value: 'number',
      currency: 'string'
    },
    expiryDate: 'string',
    metadata: {
      createdAt: 'string',
      modifiedAt: 'string',
      version: 'number'
    },
    message: 'string|null' // Context message like "Return request details"
  }
};

/**
 * Action Buttons Response
 * Used when providing interactive actions
 */
export const ActionButtonsSchema = {
  type: RESPONSE_TYPES.ACTION_BUTTONS,
  data: {
    message: 'string',
    buttons: [
      {
        id: 'string',
        label: 'string',
        action: 'string', // 'link', 'api_call', 'send_message'
        url: 'string|null', // for link actions
        apiEndpoint: 'string|null', // for api_call actions
        message: 'string|null', // for send_message actions
        style: 'string', // 'primary', 'secondary', 'danger'
        icon: 'string|null' // icon name
      }
    ]
  }
};

/**
 * Text Response (Default)
 * Used for simple text responses
 */
export const TextResponseSchema = {
  type: RESPONSE_TYPES.TEXT,
  data: {
    message: 'string',
    formatting: 'string|null' // 'markdown', 'html', 'plain'
  }
};

/**
 * Error Response
 * Used for error messages
 */
export const ErrorResponseSchema = {
  type: RESPONSE_TYPES.ERROR,
  data: {
    errorCode: 'string',
    message: 'string',
    details: 'string|null',
    canRetry: 'boolean'
  }
};

/**
 * Example responses for reference
 */
export const ExampleResponses = {
  cartSummary: {
    agentId: 'frontendAgent',
    sessionId: 'abc123',
    message: 'Here\'s your current cart summary',
    type: RESPONSE_TYPES.CART_SUMMARY,
    data: {
      totalItems: 3,
      shops: [
        {
          shopId: 'kotn',
          shopName: 'KOTN',
          shopLogo: '/logos/kotn.png',
          items: [
            {
              productId: 'hoodie-001',
              name: 'Unisex French Terry Hoodie',
              image: '/products/hoodie.jpg',
              quantity: 1,
              price: 188.00,
              currency: 'CAD'
            }
          ],
          subtotal: 188.00,
          currency: 'CAD'
        }
      ],
      grandTotal: 315.35,
      currency: 'CAD'
    },
    timestamp: '2024-01-15T10:30:00Z'
  },

  orderList: {
    agentId: 'frontendAgent',
    sessionId: 'abc123',
    message: 'Here are your recent orders',
    type: RESPONSE_TYPES.ORDER_LIST,
    data: {
      orders: [
        {
          orderId: 'ord_123',
          orderNumber: '#12345',
          date: '2024-01-10T14:30:00Z',
          status: 'Delivered',
          statusColor: '#10B981',
          total: 245.50,
          currency: 'CAD',
          itemCount: 2,
          shopName: 'KOTN',
          previewItems: [
            {
              name: 'Cotton T-Shirt',
              image: '/products/tshirt.jpg',
              quantity: 2
            }
          ]
        }
      ],
      pagination: {
        page: 1,
        totalPages: 3,
        totalItems: 25
      }
    },
    timestamp: '2024-01-15T10:30:00Z'
  }
};
